import { Badge } from "@bcc/components/ui/badge";
import { Button } from "@bcc/components/ui/button";
import { Card, CardContent } from "@bcc/components/ui/card";
import { Label } from "@bcc/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@bcc/components/ui/table";
import { useToast } from "@bcc/hooks/use-toast";
import { VendorContext } from "@client-shared/providers/vendor";
import { formatCurrency, formatDate } from "@client-shared/utils";
import type { PurchaseOrderInvoice, PurchaseOrderInvoiceLineDetails, PurchaseOrderInvoiceLines } from "@shared/types/payout";
import { ChevronDown, ChevronRight, Download, Loader2, Package } from "lucide-react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getPurchaseOrderDetailsErrorMessage,
  setExpandablePOLineItemCellTestId,
  setExpandablePOLineItemDetailsCellTestId,
  setExpandablePOLineItemDetailsHeadingTestId,
  setExpandablePOLineItemExportButtonTestId,
  setExpandablePOLineItemRowTestId,
  setExpandablePOLineItemToggleButtonTestId,
  shouldFetchPurchaseOrderDetails,
} from "./expandable-po-line-item.utils";
import { exportInvoicePOLineItems } from "./utils/invoice-export";

interface ExpandablePOLineItemProps {
  invoice: PurchaseOrderInvoice;
  line: PurchaseOrderInvoiceLines;
  currencyCode: string;
  onFetchDetails: (purchaseOrderId: string) => Promise<PurchaseOrderInvoiceLineDetails[]>;
  showShipToCountry: boolean;
  showKaffeesteuer: boolean;
  testId?: string;
}

function ExpandToggleIcon({ isLoading, isExpanded }: Readonly<{ isLoading: boolean; isExpanded: boolean }>) {
  if (isLoading) return <Loader2 className="h-4 w-4 animate-spin" />;
  return isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />;
}

function formatNullableCurrency(amount: number | null | undefined, currencyCode: string): string {
  return amount == null ? "—" : formatCurrency(amount, currencyCode);
}

interface PODetailsPanelProps {
  error: string | null;
  poDetails: PurchaseOrderInvoiceLineDetails[] | null;
  purchaseOrderId: string;
  currencyCode: string;
  isExporting: boolean;
  onExport: (e: React.MouseEvent) => void;
}

function PODetailsPanel({ error, poDetails, purchaseOrderId, currencyCode, isExporting, onExport }: Readonly<PODetailsPanelProps>) {
  const { t } = useTranslation();

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-destructive">
            <Package className="h-5 w-5" />
            <p className="text-sm font-medium">{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!poDetails || poDetails.length === 0) {
    return (
      <Card>
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground text-center py-2">{t("components.invoiceDetailDrawer.noDetails")}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-muted-foreground" />
            <h4 data-testid={setExpandablePOLineItemDetailsHeadingTestId(purchaseOrderId)} className="font-semibold text-sm">
              {t("components.invoiceDetailDrawer.orderDetails")} ({poDetails.length}{" "}
              {poDetails.length === 1 ? t("components.invoiceDetailDrawer.item") : t("components.invoiceDetailDrawer.items")})
            </h4>
          </div>
          <Button
            data-testid={setExpandablePOLineItemExportButtonTestId(purchaseOrderId)}
            onClick={onExport}
            disabled={isExporting}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            {isExporting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
            {isExporting ? t("components.invoiceDetailDrawer.export.exporting") : t("components.invoiceDetailDrawer.exportCsv")}
          </Button>
        </div>

        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-semibold">{t("components.invoiceDetailDrawer.itemNumber")}</TableHead>
                <TableHead className="font-semibold">{t("components.invoiceDetailDrawer.itemName")}</TableHead>
                <TableHead className="text-right font-semibold">{t("components.invoiceDetailDrawer.quantity")}</TableHead>
                <TableHead className="text-right font-semibold">{t("components.invoiceDetailDrawer.amount")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {poDetails.map((detail, index) => (
                <TableRow key={`${detail.itemNumber}-${index}`}>
                  <TableCell
                    data-testid={setExpandablePOLineItemDetailsCellTestId(purchaseOrderId, index, "item-number")}
                    className="font-mono text-sm"
                  >
                    {detail.itemNumber}
                  </TableCell>
                  <TableCell
                    data-testid={setExpandablePOLineItemDetailsCellTestId(purchaseOrderId, index, "item-name")}
                    className="font-medium"
                  >
                    {detail.itemName}
                  </TableCell>
                  <TableCell
                    data-testid={setExpandablePOLineItemDetailsCellTestId(purchaseOrderId, index, "quantity")}
                    className="text-right"
                  >
                    {detail.quantity}
                  </TableCell>
                  <TableCell
                    data-testid={setExpandablePOLineItemDetailsCellTestId(purchaseOrderId, index, "approved-amount")}
                    className="text-right font-semibold"
                  >
                    {formatCurrency(detail.approvedAmount, currencyCode)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="grid grid-cols-3 gap-4 pt-2">
          <div>
            <Label className="text-xs text-muted-foreground">{t("components.invoiceDetailDrawer.labels.invoiceId")}</Label>
            <p className="text-sm font-medium">{poDetails[0]?.invoiceId}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t("components.invoiceDetailDrawer.labels.invoiceDate")}</Label>
            <p className="text-sm">{poDetails[0]?.invoiceDate ? formatDate(poDetails[0].invoiceDate) : "-"}</p>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">{t("components.invoiceDetailDrawer.labels.status")}</Label>
            <Badge variant="outline" className="text-xs">
              {poDetails[0]?.invoiceStatus}
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function ExpandablePOLineItem({
  invoice,
  line,
  currencyCode,
  onFetchDetails,
  showShipToCountry,
  showKaffeesteuer,
  testId,
}: Readonly<ExpandablePOLineItemProps>) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [poDetails, setPoDetails] = useState<PurchaseOrderInvoiceLineDetails[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [{ currentVendor }] = useContext(VendorContext);

  const handleToggle = async () => {
    if (shouldFetchPurchaseOrderDetails(isExpanded, isLoading, poDetails)) {
      setIsLoading(true);
      setError(null);
      try {
        const fetchedDetails = await onFetchDetails(line.purchaseOrderId);
        setPoDetails(fetchedDetails);
      } catch (err) {
        setError(getPurchaseOrderDetailsErrorMessage(err));
      } finally {
        setIsLoading(false);
      }
    }
    setIsExpanded((previousIsExpanded) => !previousIsExpanded);
  };

  const handleExportCSV = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!poDetails || poDetails.length === 0 || !currentVendor?.id) {
      return;
    }

    setIsExporting(true);
    try {
      exportInvoicePOLineItems({
        invoice,
        line,
        poDetails,
        currencyCode,
        vendorId: currentVendor?.id,
        showShipToCountry,
        showKaffeesteuer,
        t,
      });
    } catch (error) {
      console.error("[bcc] CSV export failed:", error);
      toast({
        title: t("components.invoiceDetailDrawer.export.error"),
        description: error instanceof Error ? error.message : t("components.invoiceDetailDrawer.export.errorDescription"),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <TableRow
        data-testid={testId ?? setExpandablePOLineItemRowTestId(line.purchaseOrderId)}
        className="cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={handleToggle}
      >
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "purchase-order-id")} className="font-medium">
          <div className="flex items-center gap-2">
            <Button
              data-testid={setExpandablePOLineItemToggleButtonTestId(line.purchaseOrderId)}
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              aria-label={
                isExpanded
                  ? t("components.invoiceDetailDrawer.collapseOrderDetails")
                  : t("components.invoiceDetailDrawer.expandOrderDetails")
              }
              aria-expanded={isExpanded}
              onClick={(e) => {
                e.stopPropagation();
                void handleToggle();
              }}
            >
              <ExpandToggleIcon isLoading={isLoading} isExpanded={isExpanded} />
            </Button>
            <span>{line.purchaseOrderId}</span>
          </div>
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "order-date")}>
          {formatDate(line.orderDate)}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "ship-date")}>
          {line.shipDate ? formatDate(line.shipDate) : "-"}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "order-amount-before-tax")}>
          {formatNullableCurrency(line.orderAmountBeforeTax, currencyCode)}
        </TableCell>
        {showShipToCountry && (
          <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "ship-to-country")}>
            {line.shipToCountry ? (
              <Badge variant="outline" className="px-1.5 py-0.5 text-xs font-mono">
                {line.shipToCountry}
              </Badge>
            ) : (
              "—"
            )}
          </TableCell>
        )}
        {showKaffeesteuer && (
          <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "ks-amount")}>
            {formatNullableCurrency(line.ksAmount, currencyCode)}
          </TableCell>
        )}
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "vat-rate")}>
          {line.vatRate == null ? "—" : `${line.vatRate}%`}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "vat-amount")}>
          {formatNullableCurrency(line.vatAmount, currencyCode)}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "order-amount")} className="font-semibold">
          {formatCurrency(line.orderAmount, currencyCode)}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "shipping-fee")}>
          {formatCurrency(line.shippingFee || 0, currencyCode)}
        </TableCell>
        <TableCell data-testid={setExpandablePOLineItemCellTestId(line.purchaseOrderId, "status")}>
          <Badge variant="outline">{line.purchaseOrderStatus}</Badge>
        </TableCell>
      </TableRow>

      {isExpanded && (
        <TableRow>
          <TableCell colSpan={11 - (showShipToCountry ? 0 : 1) - (showKaffeesteuer ? 0 : 1)} className="bg-muted/30 p-0">
            <div className="p-4 space-y-4">
              <PODetailsPanel
                error={error}
                poDetails={poDetails}
                purchaseOrderId={line.purchaseOrderId}
                currencyCode={currencyCode}
                isExporting={isExporting}
                onExport={handleExportCSV}
              />
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}
